/**
 * Marketplace API client (Macro #29 Stage 3).
 *
 * Thin, backend-authoritative client over the Stage-2 Marketplace routes.
 * Reads carry the SIWE session cookie; economic writes additionally carry the
 * session-bound CSRF token (shared memory-only provider), refetching once on a
 * stale-token 403 — identical to every other owner mutation in the app.
 * The frontend is NEVER authority: every economic fact comes from these routes.
 */
import { API_BASE } from "./api";
import { clearCsrfToken, getCsrfToken } from "./territory-identity-api";

export class MarketplaceError extends Error {
  code: string;
  status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "MarketplaceError";
    this.status = status;
    this.code = code;
  }
}

interface ErrorEnvelope {
  code?: string;
  message?: string;
  detail?: { code?: string; message?: string };
}

async function parse<T>(resp: Response): Promise<T> {
  const text = await resp.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!resp.ok) {
    const env = (body ?? {}) as ErrorEnvelope;
    const code = env.code ?? env.detail?.code ?? "UNKNOWN";
    const message = env.message ?? env.detail?.message ?? resp.statusText;
    throw new MarketplaceError(resp.status, code, message);
  }
  return body as T;
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const resp = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
    signal,
  });
  return parse<T>(resp);
}

async function csrfSend<T>(method: "POST" | "DELETE", path: string, body?: Record<string, unknown>): Promise<T> {
  const payload = body ? JSON.stringify(body) : undefined;
  const doSend = async (csrf: string): Promise<Response> =>
    fetch(`${API_BASE}${path}`, {
      method,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-The-Grid-CSRF": csrf,
      },
      body: payload,
    });
  let resp = await doSend(await getCsrfToken());
  if (resp.status === 403) {
    const peek = (await resp.clone().json().catch(() => null)) as ErrorEnvelope | null;
    const code = peek?.code ?? peek?.detail?.code;
    if (code === "CSRF_INVALID" || code === "CSRF_REQUIRED") {
      clearCsrfToken();
      resp = await doSend(await getCsrfToken(true));
    }
  }
  return parse<T>(resp);
}

// ---- types ----
export interface MarketplaceCurrency {
  symbol: string;
  address: string | null;
  decimals: number;
  enabled: boolean;
}

export interface MarketplaceConfig {
  chain_id: number;
  enabled: boolean;
  offers_enabled: boolean;
  marketplace_enabled: boolean;
  marketplace_address: string | null;
  land_collection: string | null;
  artwork_enabled: boolean;
  auctions_enabled: boolean;
  signer_available: boolean;
  preview_demo: boolean;
  partial_land_sale_enabled: boolean;
  subdivision_vault: string | null;
  artwork_escrow: string | null;
  auction_escrow: string | null;
  grid_fee_bps: number;
  royalty_min_bps: number;
  royalty_max_bps: number;
  currencies: MarketplaceCurrency[];
}

export interface Listing {
  order_hash: string;
  chain_id: number;
  marketplace_address: string;
  seller: string;
  collection: string;
  token_id: number;
  currency: string;
  price_micro: number;
  listing_version: number;
  nonce: number;
  expiry_real_ts: number;
  parcel_id: number | string | null;
  cells: [number, number][];
  cellset_commitment: string;
  unit_kind: string;
  status: string;
  floor_micro?: number;
  floor_binding?: boolean;
  preview_demo?: boolean;
  parent_token_id?: number | string | null;
  child_token_id?: number | null;
  created_at?: string | null;
}

/** On-chain SettlementPermit struct (mirrors TheGridMarketplace.SettlementPermit). */
export interface SettlementPermitData {
  orderHash: string;
  buyer: string;
  floorPrice: number;
  expiryRealTs: number;
}

/**
 * Response of POST /listings/{orderHash}/permit — the authority-signed
 * settlement permit PLUS the seller's canonical Order signature. Both are
 * required by the buyer to build the on-chain Marketplace.fill().
 */
export interface PermitResponse {
  order_hash: string;
  permit: SettlementPermitData;
  signature: string;
  signer: string;
  floor_price_micro: number;
  seller_signature: string;
}

/** A parcel the session wallet may put up for sale (real: canonical parcels). */
export interface SellableParcel {
  parcel_id: number;
  x: number;
  y: number;
  w: number;
  h: number;
  cell_count: number;
  owner: string;
  collection: string;
  token_id: number;
  active_listings: number;
  preview_demo: boolean;
}

/** REAL whole-parcel listing submission (seller-signed EIP-712 order). */
export interface CreateWholeParcelListingBody {
  parcel_id: number;
  collection: string;
  token_id: number;
  currency: string;
  price_micro: number;
  listing_version: number;
  nonce: number;
  expiry_real_ts: number;
  seller_signature: string;
}

/** Read-only validation of a partial-sale cell selection (backend-authoritative). */
export interface PartialValidation {
  parcel_id: number;
  cells: [number, number][];
  cell_count: number;
  within_parent: boolean;
  contiguous: boolean;
  is_full_parcel: boolean;
  basis_micro: number | null;
  floor_micro: number | null;
  floor_binding: boolean;
  basis_missing: boolean;
  overlaps: [number, number][];
  valid: boolean;
  reasons: string[];
}

export interface DemoPartialListingBody {
  parcel_id: number;
  currency: string;
  price_micro: number;
  nonce: number;
  expiry_real_ts: number;
  rects: { x: number; y: number; w: number; h: number }[];
}

export type SubRect = { x: number; y: number; w: number; h: number };

/** Pre-computed EXACT subdivision-split plan (SELL child + REMAINDER child). */
export interface SubdivisionPlan {
  ok: boolean;
  valid: boolean;
  reasons: string[];
  parcel_id?: number;
  parent_token_id?: number;
  sell_rects?: SubRect[];
  remainder_rects?: SubRect[];
  sell_rect_count?: number;
  remainder_rect_count?: number;
  total_rects?: number;
  max_rects_per_call: number;
  exceeds_limit?: boolean;
  cells?: [number, number][];
  cell_count?: number;
  owners?: string[];
  rects_per_child?: SubRect[][];
}

export interface ReconciledChild {
  child_id: number;
  parent_token_id: number;
  owner: string;
  vault_address: string;
  rects: [number, number, number, number][];
  cells: [number, number][];
  cell_count: number;
}

/** REAL partial (wrapped vault-child) listing submission (seller-signed order). */
export interface CreatePartialListingBody {
  parcel_id: number;
  child_token_id: number;
  currency: string;
  price_micro: number;
  listing_version: number;
  nonce: number;
  expiry_real_ts: number;
  rects: SubRect[];
  seller_signature: string;
}

export interface Offer {
  offer_hash: string;
  asset_key: string;
  chain_id: number;
  offeror: string;
  currency: string;
  amount_micro: number;
  expiry_market_ts: number;
  nonce: number;
  status: string;
}

export interface ArtworkPlacement {
  anchor_x: number;
  anchor_y: number;
  width: number;
  height: number;
  rotation_deg: number;
  revision: number;
}

export interface Auction {
  auction_id: string;
  artwork_id: string;
  seller: string;
  creator: string;
  currency: string;
  reserve_micro: number;
  min_increment_micro: number;
  royalty_bps: number;
  start_market_ts: number;
  end_market_ts: number;
  anti_snipe_window: number;
  highest_bidder: string | null;
  highest_bid_micro: number;
  state: string;
  placement_locked: boolean;
  artwork_title?: string | null;
}

/** Active fixed-price artwork listing summary (public projection). */
export interface FixedPriceListing {
  price_micro: number;
  currency: string;
  seller: string;
  royalty_bps: number;
  status: string;
  buyer_binding?: string | null;
  origin_offer_hash?: string | null;
}

/** Full active fixed-price listing (from `mine` / list responses). */
export interface ArtworkListing {
  artwork_id: string;
  seller: string;
  creator: string;
  currency: string;
  price_micro: number;
  royalty_bps: number;
  status: string;
  created_at?: string | null;
}

/** One artwork provenance/sale history event (append-only). */
export interface ArtworkHistoryEvent {
  seq: number;
  kind: string;
  facts: Record<string, unknown>;
  created_at: string | null;
}

export interface ArtworkSummary {
  artwork_id: string;
  title: string;
  creator: string;
  owner: string;
  royalty_percent: number;
  width: number;
  height: number;
  footprint: number;
  status: string;
  placement: ArtworkPlacement | null;
  auction: Auction | null;
  fixed_price: FixedPriceListing | null;
}

export interface ArtworkPublic {
  artwork_id: string;
  creator: string;
  owner: string;
  owner_revision: number;
  title: string;
  description: string;
  width: number;
  height: number;
  footprint: number;
  royalty_percent: number;
  status: string;
  content_version: number;
  content_hash: string;
  frozen_at: string;
  content?: unknown;
}

export interface ArtworkDetailData {
  artwork: ArtworkPublic;
  placement: ArtworkPlacement | null;
  auction: Auction | null;
  fixed_price: FixedPriceListing | null;
  offers: Offer[];
  history: ArtworkHistoryEvent[];
}

/** On-chain ArtworkEscrow.OpenPermit struct (mirrors artwork_permits.py). */
export interface ArtworkOpenPermit {
  settlementId: string;
  artworkId: string;
  buyer: string;
  seller: string;
  creator: string;
  currency: string;
  grossPrice: number;
  royaltyBps: number;
  placementCommitment: string;
  ownerRevision: number;
  expiryRealTs: number;
}

/** Response of POST /artworks/{id}/buy — settlement + on-chain OPEN permit. */
export interface ArtworkBuyResult {
  settlement_id: string;
  artwork_id: string;
  buyer: string;
  seller: string;
  currency: string;
  gross_micro: number;
  grid_fee_micro: number;
  royalty_micro: number;
  seller_proceeds_micro: number;
  state: string;
  permit: ArtworkOpenPermit;
  signature: string;
  signer: string;
}

/** Settlement lifecycle status (polling projection). */
export interface SettlementStatus {
  settlement_id: string;
  artwork_id?: string;
  buyer?: string;
  seller?: string;
  currency?: string;
  gross_micro?: number;
  grid_fee_micro?: number;
  royalty_micro?: number;
  seller_proceeds_micro?: number;
  state: string;
  pending: boolean;
  tx_hash?: string | null;
  release_tx?: string | null;
  release_ready: boolean;
  finalize_ready: boolean;
  sold: boolean;
}

export interface AuthorizeReleaseResult {
  settlement_id: string;
  release_signature: string;
  signer: string;
  state: string;
}

/** On-chain AuctionEscrow.AuctionPermit struct (mirrors auction_permits.py). */
export interface AuctionCreatePermit {
  auctionId: string;
  artworkId: string;
  seller: string;
  creator: string;
  currency: string;
  reservePrice: number;
  minIncrement: number;
  royaltyBps: number;
  startMarketTs: number;
  endMarketTs: number;
  antiSnipWindow: number;
  placementCommitment: string;
  ownerRevision: number;
  expiryRealTs: number;
}

/** Response of POST /auctions — auction row + on-chain createAuction permit. */
export interface AuctionCreateResult extends Auction {
  permit: AuctionCreatePermit;
  signature: string;
  signer: string;
}

/** On-chain AuctionEscrow.BidPermit struct. */
export interface AuctionBidPermit {
  auctionId: string;
  bidder: string;
  amount: number;
  nonce: number;
  expiryRealTs: number;
}

export interface AuctionCancelResult {
  auction_id: string;
  cancellable: boolean;
  auction_escrow_address: string;
  state: string;
}

export interface BidResult {
  auction_id: string;
  permit: AuctionBidPermit;
  digest: string;
  signature: string;
  signer: string;
  min_acceptable_micro: number;
  anti_snipe_will_extend: boolean;
}

export interface MyBid {
  auction_id: string;
  amount_micro: number;
  artwork_title: string | null;
  auction_state: string | null;
  highest_bid_micro: number;
  end_market_ts: number;
  leading: boolean;
}

/** A completed secondary-market settlement (a seller SALE / buyer PURCHASE row). */
export interface CompletedSettlement {
  settlement_id: string;
  asset_key: string;
  asset_type: string;
  collection: string | null;
  token_id: number | null;
  seller: string;
  buyer: string;
  gross_micro: number;
  grid_fee_micro: number;
  seller_proceeds_micro: number;
  currency: string | null;
  tx_hash: string;
  status: string;
  created_at: string | null;
}

export interface MyMarketplaceData {
  wallet: string;
  listings: Listing[];
  artwork_listings: ArtworkListing[];
  my_auctions: Auction[];
  offers_made: Offer[];
  offers_received: Offer[];
  pending_purchases: Listing[];
  bids: MyBid[];
  sales: CompletedSettlement[];
  purchases: CompletedSettlement[];
  counts: {
    listings: number;
    artwork_listings: number;
    my_auctions: number;
    offers_made: number;
    offers_received: number;
    pending_purchases: number;
    bids: number;
    sales: number;
    purchases: number;
  };
  market_now: number;
}

export interface OwnerAudit {
  integrity: {
    no_free_grant_ok: boolean;
    zero_price_grant_count: number;
    zero_price_grants: { parcel_id: unknown; owner: string; kind: string; tx_hash: string }[];
  };
  supply: {
    active_listings: number;
    sold_listings: number;
    open_offers: number;
    open_auctions: number;
    pending_settlements: number;
    recorded_acquisitions: number;
  };
  private_sales: PrivateSale[];
  private_sale_cancel_supported: boolean;
  market_now: number;
}

export type PrivateSaleStatus = "active" | "completed" | "expired" | "cancelled";

export interface PrivateSale {
  sale_id: string;
  buyer: string;
  unit_price_micro: number;
  expiry_real_ts: number | null;
  executed: boolean;
  status: PrivateSaleStatus;
  cancellable: boolean;
}

/** One ownership-transfer event in a LAND parcel provenance timeline. */
export interface LandProvenanceEvent {
  seq: number;
  kind: string;
  seller: string | null;
  buyer: string | null;
  previous_owner: string | null;
  new_owner: string | null;
  price_micro: number | null;
  grid_fee_micro: number | null;
  seller_proceeds_micro: number | null;
  currency: string | null;
  tx_hash: string | null;
  created_at: string | null;
  seller_display_name_snapshot: string | null;
  buyer_display_name_snapshot: string | null;
  seller_display_name_current: string | null;
  buyer_display_name_current: string | null;
}

export interface LandProvenance {
  asset_key: string;
  events: LandProvenanceEvent[];
  count: number;
}

/** LOCKED/ineligible cells of a parcel (seller area-selection grid). */
export interface ParcelOccupancy {
  parcel_id: number;
  chain_id: number;
  x: number;
  y: number;
  w: number;
  h: number;
  cell_count: number;
  locked_cells: [number, number][];
}

export interface OwnerSupplyCheck {
  owner: string;
  owned: number;
  with_paid_origin: number;
  anomalies: { parcel_id: unknown; reason: string }[];
  consistent: boolean;
}

export interface OwnerActionCapability {
  available: boolean;
  reason: string | null;
}

export interface OwnerCapabilities {
  chain_id: number;
  actions: {
    release: OwnerActionCapability;
    relock: OwnerActionCapability;
    private_sale: OwnerActionCapability;
  };
  pricing_law: { tiers: string[]; grid_zero_manual: boolean; min_price_micro: number };
}

export const marketplaceApi = {
  config: (signal?: AbortSignal) => get<MarketplaceConfig>("/api/v1/marketplace/config", signal),
  listings: (params: { status?: string; limit?: number } = {}, signal?: AbortSignal) => {
    const q = new URLSearchParams();
    if (params.status) q.set("status", params.status);
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return get<{ listings: Listing[]; count: number }>(
      `/api/v1/marketplace/listings${qs ? `?${qs}` : ""}`,
      signal,
    );
  },
  listing: (orderHash: string, signal?: AbortSignal) =>
    get<Listing>(`/api/v1/marketplace/listings/${encodeURIComponent(orderHash)}`, signal),
  assetOffers: (assetKey: string, signal?: AbortSignal) =>
    get<{ asset_key: string; offers: Offer[]; count: number }>(
      `/api/v1/marketplace/assets/${encodeURIComponent(assetKey)}/offers`,
      signal,
    ),
  prepareBuy: (orderHash: string, ttlRealSeconds = 1800) =>
    csrfSend<{ order_hash: string; buyer: string; floor_micro: number; reservation_expires_real_ts: number; locked_cells: number }>(
      "POST",
      `/api/v1/marketplace/listings/${encodeURIComponent(orderHash)}/prepare-buy`,
      { ttl_real_seconds: ttlRealSeconds },
    ),
  requestPermit: (orderHash: string, permitTtlSeconds = 1800) =>
    csrfSend<PermitResponse>(
      "POST",
      `/api/v1/marketplace/listings/${encodeURIComponent(orderHash)}/permit`,
      { permit_ttl_seconds: permitTtlSeconds },
    ),
  createOffer: (body: { asset_key: string; currency: string; amount_micro: number; expiry_market_ts: number; nonce: number }) =>
    csrfSend<Offer>("POST", "/api/v1/marketplace/offers", body),
  withdrawOffer: (offerHash: string) =>
    csrfSend<{ offer_hash: string; status: string }>("DELETE", `/api/v1/marketplace/offers/${encodeURIComponent(offerHash)}`),
  acceptOffer: (
    offerHash: string,
    body: { seller_signature: string; nonce: number; listing_version?: number; expiry_real_ts?: number },
  ) =>
    csrfSend<{ offer_hash: string; status: string; acceptance_order_hash: string; listing: Listing; buyer: string; amount_micro: number }>(
      "POST",
      `/api/v1/marketplace/offers/${encodeURIComponent(offerHash)}/accept`,
      body as unknown as Record<string, unknown>,
    ),
  declineOffer: (offerHash: string) =>
    csrfSend<{ offer_hash: string; status: string }>(
      "POST",
      `/api/v1/marketplace/offers/${encodeURIComponent(offerHash)}/decline`,
      {},
    ),
  acceptArtworkOffer: (offerHash: string) =>
    csrfSend<{ offer_hash: string; status: string; kind: string; artwork_id: string; buyer: string; amount_micro: number; listing: FixedPriceListing }>(
      "POST",
      `/api/v1/marketplace/offers/${encodeURIComponent(offerHash)}/accept`,
      {},
    ),
  cancelListing: (orderHash: string) =>
    csrfSend<{ order_hash: string; status: string }>("DELETE", `/api/v1/marketplace/listings/${encodeURIComponent(orderHash)}`),
  createListing: (body: CreateWholeParcelListingBody) =>
    csrfSend<Listing>("POST", "/api/v1/marketplace/listings", body as unknown as Record<string, unknown>),
  artworks: (signal?: AbortSignal) =>
    get<{ artworks: ArtworkSummary[]; count: number }>("/api/v1/marketplace/artworks", signal),
  artwork: (artworkId: string, signal?: AbortSignal) =>
    get<ArtworkDetailData>(`/api/v1/marketplace/artworks/${encodeURIComponent(artworkId)}`, signal),
  auctions: (signal?: AbortSignal) =>
    get<{ auctions: Auction[]; count: number; market_now: number }>("/api/v1/marketplace/auctions", signal),
  auction: (auctionId: string, signal?: AbortSignal) =>
    get<Auction & { market_now: number }>(`/api/v1/marketplace/auctions/${encodeURIComponent(auctionId)}`, signal),
  placeBid: (auctionId: string, body: { amount_micro: number; nonce: number }) =>
    csrfSend<BidResult>("POST", `/api/v1/marketplace/auctions/${encodeURIComponent(auctionId)}/bid`, body),
  mine: (signal?: AbortSignal) => get<MyMarketplaceData>("/api/v1/marketplace/mine", signal),
  landProvenance: (assetKey: string, signal?: AbortSignal) =>
    get<LandProvenance>(
      `/api/v1/marketplace/land/provenance?asset_key=${encodeURIComponent(assetKey)}`, signal),
  parcelOccupancy: (parcelId: number, signal?: AbortSignal) =>
    get<ParcelOccupancy>(
      `/api/v1/marketplace/land/${encodeURIComponent(String(parcelId))}/occupancy`, signal),
  // --- partial LAND sale (Corrective A) + PREVIEW demo seller flow ---
  mySellableLand: (signal?: AbortSignal) =>
    get<{ parcels: SellableParcel[]; preview_demo: boolean }>(
      "/api/v1/marketplace/land/my-sellable", signal),
  validatePartial: (body: { parcel_id: number; rects: { x: number; y: number; w: number; h: number }[] }) =>
    csrfSend<PartialValidation>("POST", "/api/v1/marketplace/land/validate-partial", body),
  createDemoPartialListing: (body: DemoPartialListingBody) =>
    csrfSend<Listing & { basis_micro?: number }>(
      "POST", "/api/v1/marketplace/land/preview-demo-listings", body as unknown as Record<string, unknown>),
  planSubdivision: (body: { parcel_id: number; rects: SubRect[] }) =>
    csrfSend<SubdivisionPlan>("POST", "/api/v1/marketplace/land/plan-subdivision", body as unknown as Record<string, unknown>),
  reconcileSubdivisionChild: (childTokenId: number) =>
    csrfSend<ReconciledChild>("POST", "/api/v1/marketplace/land/subdivision/reconcile-child", { child_token_id: childTokenId }),
  createPartialListing: (body: CreatePartialListingBody) =>
    csrfSend<Listing>("POST", "/api/v1/marketplace/land/partial-listings", body as unknown as Record<string, unknown>),
  ownerCapabilities: (signal?: AbortSignal) =>
    get<OwnerCapabilities>("/api/v1/owner/capabilities", signal),
  ownerAudit: (signal?: AbortSignal) => get<OwnerAudit>("/api/v1/owner/audit", signal),
  ownerSupplyCheck: (owner: string, signal?: AbortSignal) =>
    get<OwnerSupplyCheck>(`/api/v1/owner/audit/supply-check?owner=${encodeURIComponent(owner)}`, signal),
  ownerAction: (action: "release" | "relock" | "private-sale", body: Record<string, unknown>) =>
    csrfSend<{ ok: boolean }>("POST", `/api/v1/owner/actions/${action}`, body),
  cancelPrivateSale: (saleId: string) =>
    csrfSend<{ ok: boolean }>("POST", `/api/v1/owner/private-sale/${encodeURIComponent(saleId)}/cancel`),
  // --- artwork FIXED-PRICE (Phase 7-8) ---
  listArtwork: (artworkId: string, body: { currency: string; price_micro: number }) =>
    csrfSend<ArtworkListing>("POST", `/api/v1/marketplace/artworks/${encodeURIComponent(artworkId)}/list`, body),
  cancelArtworkListing: (artworkId: string) =>
    csrfSend<{ artwork_id: string; status: string }>("DELETE", `/api/v1/marketplace/artworks/${encodeURIComponent(artworkId)}/list`),
  buyArtwork: (artworkId: string, body: { nonce: number; permit_ttl_seconds?: number }) =>
    csrfSend<ArtworkBuyResult>("POST", `/api/v1/marketplace/artworks/${encodeURIComponent(artworkId)}/buy`, body),
  // --- artwork settlement lifecycle (Phase 8) ---
  getSettlement: (settlementId: string, signal?: AbortSignal) =>
    get<SettlementStatus>(`/api/v1/marketplace/settlements/${encodeURIComponent(settlementId)}`, signal),
  authorizeRelease: (settlementId: string) =>
    csrfSend<AuthorizeReleaseResult>("POST", `/api/v1/marketplace/settlements/${encodeURIComponent(settlementId)}/authorize-release`),
  finalizeSettlement: (settlementId: string) =>
    csrfSend<{ settlement_id: string; state: string; new_owner: string }>(
      "POST", `/api/v1/marketplace/settlements/${encodeURIComponent(settlementId)}/finalize`),
  // --- auction create / cancel (Phase 9) ---
  createAuction: (body: {
    artwork_id: string; currency: string; reserve_micro: number; min_increment_micro?: number;
    start_market_ts: number; end_market_ts: number; anti_snipe_window?: number; nonce?: number;
  }) => csrfSend<AuctionCreateResult>("POST", "/api/v1/marketplace/auctions", body),
  cancelAuction: (auctionId: string) =>
    csrfSend<AuctionCancelResult>("POST", `/api/v1/marketplace/auctions/${encodeURIComponent(auctionId)}/cancel`, {}),
};

// asset_key builders mirror the backend.
export function artworkAssetKey(artworkId: string): string {
  return `artwork:${artworkId}`;
}

// asset_key builder mirrors the backend: land:{chain}:{collection}:{token}
export function landAssetKey(chainId: number, collection: string, tokenId: number): string {
  return `land:${chainId}:${collection.toLowerCase()}:${tokenId}`;
}
